import React, { useMemo } from 'react'
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid'
import { Link } from 'gatsby'
import { cx } from 'class-variance-authority'
import { getPageLinks } from '../../../Utils/pagination'

interface Props {
  totalPages: number
  currentPage: number
  baseUrl: string
  isFirst?: boolean
  isLast?: boolean
  getNextPage?: (pageToMoveOn: number, baseUrl: string) => string
}

const PaginationLink = ({
  children,
  to,
  isCurrent = false,
}: {
  children: React.ReactNode
  to: string
  isCurrent?: boolean
}) => (
  <Link
    to={to}
    className={cx(
      'inline-flex items-center pt-4 pl-1 font-medium border-t-2 disabled:cursor-not-allowed',
      isCurrent
        ? 'text-night border-night'
        : 'text-gray-500 hover:border-gray-300 hover:text-gray-700 border-transparent',
    )}
    aria-current={isCurrent ? 'page' : undefined}
    aria-disabled={isCurrent}
    aria-label={`Page — ${children}`}
    title={`Page — ${children}`}
  >
    {children}
  </Link>
)

function defaultGetNextPage(pageToMoveOn: number, baseUrl: string) {
  return pageToMoveOn === 1 ? baseUrl : `${baseUrl}/${pageToMoveOn}`
}

export const Pagination = ({
  totalPages,
  currentPage,
  baseUrl,
  isFirst = false,
  isLast = false,
  getNextPage = defaultGetNextPage,
}: Props) => {
  if (totalPages <= 1) {
    return null
  }

  const paginationLinks = useMemo(
    () => getPageLinks({ totalPages, currentPage }),
    [totalPages, currentPage],
  )

  return (
    <nav className="flex items-center justify-between px-4 sm:px-0">
      <div className="flex-1 hidden md:flex">
        {!isFirst && (
          <Link
            to={getNextPage(currentPage - 1, baseUrl)}
            className="inline-flex items-center pt-4 font-medium text-night hover:text-gray-700 disabled:cursor-not-allowed"
            aria-disabled={currentPage === 1}
            aria-label="Précédent"
            title="Précédent"
          >
            <ArrowLeftIcon
              aria-hidden="true"
              className="w-5 h-5 mr-3 text-night"
            />
            Précédent
          </Link>
        )}
      </div>
      <div className="justify-center flex-1 hidden space-x-4 md:-mt-px md:flex">
        {paginationLinks.map((page, index) =>
          page === '...' ? (
            // eslint-disable-next-line react/no-array-index-key
            <span
              key={`${page}-${index}`}
              className="inline-flex items-center pt-4 pl-1 font-medium text-gray-500"
            >
              ...
            </span>
          ) : (
            <PaginationLink
              key={page}
              to={getNextPage(page as number, baseUrl)}
              isCurrent={page === currentPage}
            >
              {page}
            </PaginationLink>
          ),
        )}
      </div>

      <div className="flex-1 hidden w-0 -mt-px text-right md:flex md:justify-end">
        {!isLast && (
          <Link
            to={getNextPage(currentPage + 1, baseUrl)}
            className="inline-flex items-center pt-4 font-medium text-night hover:text-gray-700 disabled:cursor-not-allowed"
            aria-disabled={currentPage === totalPages}
            aria-label="Suivant"
            title="Suivant"
          >
            Suivant
            <ArrowRightIcon
              aria-hidden="true"
              className="w-5 h-5 ml-3 text-night"
            />
          </Link>
        )}
      </div>
    </nav>
  )
}
